import React from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, Check, CreditCard, ShieldCheck, Star, Zap } from "lucide-react";
import TypeShowcase from "../../../components/landing/TypeShowcase";
import type { ListingSubcategory } from "../../../components/landing/ListingGrid";
import { staySearchUrl } from "./StayIntro";

const HOTEL_FEATURES = [
  { icon: ShieldCheck, label: "Verified" },
  { icon: Zap, label: "Instant Booking" },
  { icon: CreditCard, label: "Secure Payment" },
  { icon: Star, label: "Reviews" },
];

const hotelFooter = (
  <div className="flex flex-wrap items-center gap-x-[16px] gap-y-1">
    {HOTEL_FEATURES.map(({ icon: Icon, label }) => (
      <span key={label} className="flex items-center gap-1 text-[12px] font-semibold leading-4 text-[#0a0a0a]">
        <Icon className="size-[11px] text-[#017bef]" />
        {label}
      </span>
    ))}
  </div>
);

const apartmentFooter = (
  <div className="flex items-center justify-between py-1">
    <div className="text-[12px] leading-4">
      <p className="font-bold text-[#0a0a0a]">More space. More flexibility.</p>
      <p className="pt-0.5 text-[#717182]">More like home.</p>
    </div>
    <span className="size-2 rounded-full bg-[#22c55e]" />
  </div>
);

const BOUTIQUE_INCLUDED = [
  "Curated boutique and independent properties",
  "Distinctive design and character",
  "Personalised guest service",
  "Local recommendations and experiences",
  "Verified reviews",
  "Unique ambience and architecture",
];

const BOUTIQUE_TAGS = ["Memorable stays", "Local character", "Personal service", "Distinctive design"];

function BoutiqueSection({ subcategories }: StayTypesProps) {
  const history = useHistory();

  return (
    <section className="relative overflow-hidden bg-[#012d6e] py-20">
      <div className="pointer-events-none absolute right-0 top-0 h-[400px] w-[500px] rounded-full bg-[#017bef]/[0.12] blur-[64px]" />

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-[16px] sm:px-8 lg:grid-cols-2">
        <div className="relative h-[360px] overflow-hidden rounded-3xl sm:h-[480px]">
          <img
            src="/figma-assets/stay/intro-boutique.jpg"
            alt="Boutique Hotels"
            className="absolute inset-0 size-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{ backgroundImage: "linear-gradient(0deg, rgba(1,45,110,0.6) 0%, rgba(1,45,110,0) 50%)" }}
          />
          <span className="absolute left-[20px] top-[20px] rounded-full border-[1px] border-solid border-white/30 bg-white/10 px-[12px] py-[5px] text-[12px] font-bold uppercase leading-4 tracking-[1.2px] text-white">
            Boutique Hotels
          </span>
          <div className="absolute bottom-[20px] right-[20px] size-32 overflow-hidden rounded-2xl border-2 border-solid border-white/30 p-0.5 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)]">
            <img
              src="/figma-assets/stay/boutique-detail.jpg"
              alt="Boutique detail"
              className="size-full rounded-[14px] object-cover"
            />
          </div>
        </div>

        <div>
          <span className="inline-block rounded-full border-[1px] border-solid border-white/15 bg-white/10 px-[16px] py-1.5 text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-white/70">
            SPACIA STAY
          </span>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-white sm:text-[36px]">
            Boutique Hotels
          </h2>
          <p className="pt-[16px] text-[16px] leading-[26px] text-white/60">
            Unique, characterful properties for travellers looking for something different.
          </p>

          <p className="pt-8 text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-white/40">
            What you get
          </p>
          <ul className="space-y-2.5 pt-[16px]">
            {BOUTIQUE_INCLUDED.map(item => (
              <li key={item} className="flex items-start gap-2.5 text-[14px] leading-5 text-white/70">
                <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-white/10">
                  <Check className="size-2.5 text-white/60" />
                </span>
                {item}
              </li>
            ))}
          </ul>

          <ul className="grid grid-cols-2 gap-[12px] py-8">
            {BOUTIQUE_TAGS.map(tag => (
              <li key={tag} className="flex items-center gap-2 text-[14px] leading-5 text-white/60">
                <span className="size-1.5 shrink-0 rounded-full bg-[#60a5fa]" />
                {tag}
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => history.push(staySearchUrl("boutique", subcategories))}
            className="inline-flex items-center gap-2 text-[14px] font-semibold leading-5 text-[#60a5fa] hover:underline"
          >
            Explore Boutique Hotels
            <ArrowRight className="size-[15px]" />
          </button>
        </div>
      </div>
    </section>
  );
}

type StayTypesProps = { subcategories: ListingSubcategory[] };

export default function StayTypes({ subcategories }: StayTypesProps) {
  const history = useHistory();

  return (
    <>
      <TypeShowcase
        eyebrow="SPACIA STAY"
        name="Hotels"
        color="#017bef"
        background="bg-[#f8fbff]"
        image="/figma-assets/stay/hotels.jpg"
        imageSide="left"
        imageHeight={440}
        gradient="strong"
        imageFooter={hotelFooter}
        description="Comfortable, reliable accommodation for business and leisure travel."
        included={[
          "A wide range of hotel partners",
          "Instant online booking",
          "Flexible booking options",
          "Verified reviews and ratings",
          "Secure online payments",
          "Bookable meeting and conference facilities where available",
          "24-hour reception and guest support where available",
        ]}
        whyItWorks={[
          "Trusted, quality accommodation, wherever you travel",
          "Competitive rates and transparent pricing",
          "Simple, secure booking from one platform",
          "Convenient access to accommodation close to business districts",
        ]}
        cta="Explore Hotels"
        onCta={() => history.push(staySearchUrl("hotels", subcategories))}
        checkTint="1a"
      />
      <TypeShowcase
        eyebrow="SPACIA STAY"
        name="Apartments"
        color="#0062c0"
        background="bg-white"
        image="/figma-assets/stay/intro-apartments.jpg"
        imageSide="right"
        imageHeight={440}
        gradient="soft"
        imageFooter={apartmentFooter}
        description="Serviced apartments for extended stays, relocations and a home away from home."
        included={[
          "Fully furnished apartments",
          "Kitchen and laundry facilities",
          "Flexible length of stay",
          "Secure residential environments",
          "Business and family-friendly options",
          "Housekeeping services where available",
          "Verified listings",
        ]}
        whyItWorks={[
          "More space, comfort and privacy",
          "Cost-effective for longer stays",
          "Ideal for relocations and extended assignments",
          "Suitable for business travellers, families and remote workers",
        ]}
        cta="Explore Apartments"
        onCta={() => history.push(staySearchUrl("apartments", subcategories))}
        checkTint="1a"
      />
      <BoutiqueSection subcategories={subcategories} />
    </>
  );
}
